<!-- src/pages/LandingPageUmrahRamadanSaudi.vue -->
<template>
  <div class="min-h-screen bg-[#27272a] text-white overflow-x-hidden">
    <!-- ===== HERO ===== -->
    <section
      class="relative"
      :style="{
        backgroundImage: `linear-gradient(rgba(39,39,42,.1), rgba(39,39,42,.88)), url(${heroImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
      }"
    >
      <!-- top bar -->
      <div class="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 md:px-6">
        <div class="flex items-center gap-3">
          <div class="bg-white rounded-xl p-2 shadow-lg flex items-center justify-center">
            <img 
              :src="vwLogo"
              alt="Vehitra World"
              class="h-10 w-auto object-contain"
            />
          </div>
        </div>
        <div class="text-[15px] opacity-80">
          24/7 Support · WhatsApp: +973 3214 1440
        </div>
      </div>

      <div
        class="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-4 py-8 md:grid-cols-2 md:px-6 md:py-14"
      >
        <!-- LEFT SIDE CONTENT -->
        <div class="flex flex-col justify-center">

          <!-- Eyebrow -->
          <span
            class="mb-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-[12px] font-semibold text-emerald-300 ring-1 ring-white/15"
          >
            ● Premium Saudi Umrah & Ramadan Transfers
          </span>

          <!-- Main Heading -->
          <h1 class="leading-tight font-extrabold text-white">
            <span class="block text-4xl md:text-5xl">
              Premium Ramadan &
            </span>
            <span class="block text-4xl md:text-5xl">
              Umrah Transfers
            </span>
            <span class="block text-4xl md:text-5xl text-emerald-300">
              in Saudi Arabia
            </span>
          </h1>

          <!-- Subtext -->
          <p class="mt-6 max-w-xl text-sm md:text-base text-white/80">
            Luxury chauffeur and airport transfers for Umrah pilgrims, families, VIP travelers, and corporate guests during Ramadan.
          </p>

          <p class="mt-3 max-w-xl text-sm md:text-base text-white/80 font-medium">
            Serving Jeddah, Makkah & Madinah with premium vehicles and trained chauffeurs.
          </p>

          <!-- 6 Features -->
          <div class="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 text-white/80 text-sm">
            <div class="flex items-center gap-2">
              <span class="text-emerald-400 text-lg">✓</span> 24/7 booking & customer support
            </div>
            <div class="flex items-center gap-2">
              <span class="text-emerald-400 text-lg">✓</span> Airport Meet & Greet assistance
            </div>
            <div class="flex items-center gap-2">
              <span class="text-emerald-400 text-lg">✓</span> Professional, licensed chauffeurs
            </div>
            <div class="flex items-center gap-2">
              <span class="text-emerald-400 text-lg">✓</span> Clean, premium vehicles
            </div>
            <div class="flex items-center gap-2">
              <span class="text-emerald-400 text-lg">✓</span> Fixed & transparent pricing
            </div>
            <div class="flex items-center gap-2">
              <span class="text-emerald-400 text-lg">✓</span> On-time, safe, and comfortable travel
            </div>
          </div>

          <!-- Buttons -->
          <div class="mt-8 flex flex-wrap gap-3">
            <button
              @click="scrollToForm"
              class="inline-flex items-center justify-center rounded-full bg-emerald-500 px-6 py-3 text-sm font-semibold text-[#050708] transition hover:bg-emerald-400"
            >
              Get Instant Quote
            </button>

            <a
              :href="whatsappUrl"
              target="_blank"
              class="inline-flex items-center justify-center rounded-full border border-white/20 px-5 py-3 text-sm font-semibold text-white/90 hover:bg-white/5"
            >
              WhatsApp Our Team
            </a>
          </div>

        </div>

        <!-- RIGHT SIDE — BOOKING FORM -->
        <div class="lead-card-wrap">
          <div class="lead-card">
            <h2 class="text-[20px] font-semibold">Get a Quote for Saudi Umrah Transfers</h2>
            <p class="mt-1 text-[12px] text-white/70">
              Complete the {{ currentStep === 3 ? 'final ' : '' }}step to get your priority confirmed quote.
            </p>

            <!-- Progress Bar -->
            <div class="mt-4 flex items-center gap-2">
              <div 
                v-for="step in 3" 
                :key="step"
                class="h-1.5 flex-1 rounded-full transition-all duration-500"
                :class="step <= currentStep ? 'bg-emerald-500' : 'bg-white/10'"
              ></div>
              <span class="text-[10px] font-bold text-white/40 ml-2 uppercase tracking-widest">Step {{ currentStep }}/3</span>
            </div>

            <form
              @submit.prevent="submitLead"
              class="lead-form mt-6 flex flex-col gap-6"
            >
              <!-- STEP 1: Traveler Details -->
              <div v-if="currentStep === 1" class="flex flex-col gap-6 animate-in slide-in-from-right-4 duration-300">
                <TextField
                  label="Full Name"
                  placeholder="e.g. John Doe"
                  v-model="form.name"
                />
                <TextField
                  label="Mobile / WhatsApp Number"
                  placeholder="+966 5X XXX XXXX"
                  v-model="form.phone"
                />
                <TextField
                  label="Email Address"
                  placeholder="name@company.com"
                  v-model="form.email"
                />
              </div>

              <!-- STEP 2: Trip Information -->
              <div v-if="currentStep === 2" class="flex flex-col gap-6 animate-in slide-in-from-right-4 duration-300">
                <div class="field-reset min-w-0">
                  <LocationField
                    class="field-reset af-loc"
                    :layout="'simple'"
                    :drive="'distance'"
                    :hasError="false"
                    :data="pickUp"
                    type="pickUp"
                    label="Pickup Location (Airport / Hotel)"
                    @addressSelected="addr => onAddressSelected(addr, 'pickUp')"
                  />
                </div>
                <div class="field-reset min-w-0">
                  <LocationField
                    class="field-reset af-loc"
                    :layout="'simple'"
                    :drive="'distance'"
                    :hasError="false"
                    :data="dropOff"
                    type="dropOff"
                    label="Destination (Makkah / Madinah / City)"
                    @addressSelected="addr => onAddressSelected(addr, 'dropOff')"
                  />
                </div>
                <!-- Date / Time -->
                <div class="grid grid-cols-2 gap-4">
                  <DateField
                    class="field-reset af-dt"
                    :layout="'simple'"
                    :hasError="false"
                    :data="pickUp.date"
                    label="Pickup Date"
                    @dateChanged="onDateChanged"
                  />
                  <TimeField
                    class="field-reset af-dt"
                    :layout="'simple'"
                    :hasError="false"
                    :data="pickUp.time"
                    label="Pickup Time"
                    @timeChanged="onTimeChanged"
                  />
                </div>
              </div>

              <!-- STEP 3: Vehicle & Extras -->
              <div v-if="currentStep === 3" class="flex flex-col gap-6 animate-in slide-in-from-right-4 duration-300">
                <SelectField
                  label="Preferred Vehicle (Optional)"
                  v-model="form.carType"
                  :options="carTypeOptions"
                  placeholder="Choose vehicle type"
                />
                <div class="grid grid-cols-2 gap-4">
                  <NumberField
                    label="No. of Passengers"
                    v-model.number="form.passengers"
                    min="0"
                  />
                  <NumberField
                    label="No. of Suitcases"
                    v-model.number="form.suitcases"
                    min="0"
                  />
                </div>
                <TextArea
                  label="Additional Notes (Optional)"
                  placeholder="Flight number, child seat, special requests..."
                  v-model="form.notes"
                />
              </div>

              <!-- Navigation -->
              <div class="mt-4 flex flex-col gap-3">
                <div class="flex gap-3">
                  <button 
                    v-if="currentStep > 1"
                    type="button"
                    @click="currentStep--"
                    class="flex-1 rounded-full border border-white/20 px-6 py-4 text-[15px] font-bold text-white transition hover:bg-white/5 active:scale-95"
                  >
                    Back
                  </button>
                  
                  <button 
                    v-if="currentStep < 3"
                    type="button"
                    @click="validateAndNext"
                    class="flex-[2] rounded-full bg-emerald-500 px-6 py-4 text-[15px] font-bold text-[#050708] shadow-lg shadow-emerald-500/20 transition hover:bg-emerald-400 active:scale-95"
                  >
                    Continue to Step {{ currentStep + 1 }}
                  </button>

                  <button 
                    v-if="currentStep === 3"
                    type="submit" 
                    :disabled="submitting" 
                    class="flex-[2] btn-primary py-4 text-[15px]"
                  >
                    {{ submitting ? 'Submitting…' : 'Get Quote' }}
                  </button>
                </div>
                
                <p v-if="currentStep === 3" class="text-center text-[10px] text-white/40 uppercase tracking-tighter">
                  Instant confirmation via WhatsApp following submission
                </p>
              </div>
            </form>
          </div>
        </div>
        <!-- ===== /Right ===== -->
      </div>
    </section>

    <!-- ===== CHOOSE YOUR VEHICLE ===== -->
    <section class="bg-[#18181b] px-6 py-14">
      <div class="mx-auto max-w-6xl">
        <div class="text-center">
          <h2 class="text-3xl md:text-4xl font-extrabold text-white">
            Choose Your Vehicle
          </h2>
          <p class="mt-3 text-sm md:text-base text-zinc-300">
            Select the ideal vehicle class for your Umrah journey and transfers.
          </p>
        </div>

        <div
          class="mt-10 grid grid-cols-1 gap-6 md:grid-cols-4"
        >
          <article
            v-for="car in vehicleCards"
            :key="car.name"
            class="overflow-hidden rounded-3xl bg-[#27272a] border border-white/5 shadow-[0_24px_60px_rgba(0,0,0,.4)] transition-all duration-500 hover:border-white/10"
          >
            <!-- Image -->
            <div class="relative h-48 md:h-52 overflow-hidden">
              <img
                :src="car.image"
                :alt="car.name"
                class="h-full w-full object-cover"
              />
              <div
                class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"
              />
              <h3
                class="absolute bottom-4 left-5 text-base md:text-lg font-semibold text-white drop-shadow-lg"
              >
                {{ car.name }}
              </h3>
            </div>

            <!-- Content -->
            <div class="px-6 pb-6 pt-5">
              <ul class="text-xs text-white/70 leading-relaxed space-y-2">
                <li v-for="desc in car.description" :key="desc" class="flex gap-2">
                  <span class="text-emerald-400 italic">●</span> {{ desc }}
                </li>
              </ul>

              <div class="mt-4 flex flex-wrap gap-2">
                <span
                  v-for="tag in car.tags"
                  :key="tag"
                  class="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white/60"
                >
                  {{ tag }}
                </span>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- ===== POPULAR ROUTES ===== -->
    <section class="bg-[#18181b] px-6 pb-16">
      <div class="mx-auto max-w-6xl">
        
        <!-- Section heading -->
        <div class="text-left">
          <h2 class="text-3xl md:text-4xl font-extrabold text-white">
            Popular Ramadan & Umrah Routes
          </h2>
          <p class="mt-3 text-sm md:text-base text-zinc-300">
            Dedicated transfers between airports and holy cities.
          </p>
        </div>

        <!-- Cards grid -->
        <div class="mt-10 grid grid-cols-1 gap-6 md:grid-cols-4">

          <!-- Loop card -->
          <article
            v-for="route in routeCards"
            :key="route.name"
            class="overflow-hidden rounded-3xl bg-[#27272a] border border-white/5 shadow-[0_24px_60px_rgba(0,0,0,.4)] transition-all duration-500"
          >
            <div class="relative h-64 overflow-hidden">
              <!-- IMAGE -->
              <img :src="route.image" :alt="route.name" class="h-full w-full object-cover" />
              <!-- GRADIENT -->
              <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>

              <!-- TAG PILLS -->
              <div class="absolute left-6 top-6 flex items-center gap-2 z-20">
                <span
                  class="rounded-xl border border-emerald-400/50 bg-emerald-950/80
                         px-3 py-1 text-[10px] font-bold text-emerald-300
                         backdrop-blur-md shadow-lg"
                >
                  Highly Recommended
                </span>
              </div>

              <!-- TITLE -->
              <h3 class="absolute left-6 bottom-6 text-[17px] font-semibold text-white drop-shadow-xl z-20 leading-tight">
                {{ route.name }}
              </h3>
            </div>
          </article>

        </div>
      </div>
    </section>

    <!-- ===== WHY CHOOSE VEHITRA WORLD ===== -->
    <section class="bg-[#18181b] px-6 py-16">
      <div class="mx-auto max-w-6xl">
        <div class="text-center mb-10">
          <h2 class="text-3xl md:text-4xl font-extrabold text-white">
            Why Choose Vehitra World
          </h2>
          <p class="mt-3 text-sm md:text-base text-zinc-300">
            Trusted partner for premium ground transportation in Saudi Arabia.
          </p>
        </div>

        <div class="grid grid-cols-1 gap-6 md:grid-cols-3">
          <div
            v-for="item in whyCards"
            :key="item.title"
            class="why-card group relative p-8 rounded-3xl overflow-hidden"
          >
            <!-- Glossy Background Layer -->
            <div class="absolute inset-0 bg-white/[0.03] backdrop-blur-md border border-white/10 group-hover:bg-white/[0.06] group-hover:border-white/20 transition-all duration-500"></div>
            
            <!-- Glow Effect on Hover -->
            <div class="absolute -inset-px bg-gradient-to-br from-emerald-500/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

            <div class="relative z-10">
              <!-- Title -->
              <h3 class="text-[17px] font-bold text-white mb-3 group-hover:text-emerald-400 transition-colors duration-300">
                {{ item.title }}
              </h3>

              <!-- Copy -->
              <p class="text-[14px] text-white/60 leading-relaxed group-hover:text-white/80 transition-colors duration-300">
                {{ item.copy }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== TESTIMONIALS ===== -->
    <section class="bg-[#18181b] px-6 pb-16 pt-10">
      <div class="mx-auto max-w-6xl">
        <!-- Section Header -->
        <div class="mb-12 text-center">
          <h2 class="text-3xl md:text-4xl font-extrabold text-white">
            What Our Clients Say
          </h2>
          <div class="mt-2 mx-auto w-24 h-1 bg-emerald-500 rounded-full"></div>
          <p class="mt-4 text-sm md:text-base text-zinc-400">
            Providing peace of mind for pilgrims and travelers across the Kingdom.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div 
            v-for="(testimonial, tIdx) in [
              { quote: 'Flawless coordination for our Umrah group. The Mercedes V-Class was perfect for the family and luggage.', author: 'Umrah Travel Agent', location: 'Jeddah' },
              { quote: 'Exceptional meet and greet service at Jeddah airport. The chauffeur was professional and very helpful.', author: 'Family Pilgrim (VIP)', location: 'Makkah' },
              { quote: 'Regularly use Vehitra for our corporate transfers in Riyadh and Madinah. Timely and high-quality vehicles.', author: 'Corporate Delegate', location: 'Riyadh' }
            ]" 
            :key="tIdx"
            class="relative p-8 rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-md hover:border-emerald-500/30 transition-all duration-300"
          >
            <div class="absolute top-6 right-8 text-4xl text-emerald-500/20 font-serif">“</div>
            <p class="text-white/80 text-[15px] leading-relaxed italic relative z-10">
              “{{ testimonial.quote }}”
            </p>
            <div class="mt-6">
              <div class="font-bold text-white text-base">{{ testimonial.author }}</div>
              <div class="text-xs text-emerald-400/70 uppercase tracking-widest mt-1">{{ testimonial.location }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== FAQ ===== -->
    <section class="bg-[#18181b] px-6 py-20 border-t border-white/5">
      <div class="mx-auto max-w-3xl">
        <div class="text-center mb-12">
          <h2 class="text-3xl md:text-4xl font-extrabold text-white">
            Frequently Asked Questions
          </h2>
          <div class="mt-2 mx-auto w-24 h-1 bg-emerald-500 rounded-full"></div>
        </div>

        <div class="mt-10 space-y-5">
          <div 
            v-for="(faq, idx) in faqs" 
            :key="idx" 
            class="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-sm overflow-hidden"
            :class="faq.open ? 'border-emerald-500/30 bg-white/[0.04]' : ''"
          >
            <button 
              class="w-full flex justify-between items-center px-6 py-5 text-left transition-colors"
              @click="faq.open = !faq.open"
            >
              <span 
                class="font-bold text-[15px] transition-colors"
                :class="faq.open ? 'text-emerald-400' : 'text-white/90'"
              >
                {{ faq.q }}
              </span>
              <span 
                class="text-emerald-500 transition-transform duration-300" 
                :class="faq.open ? 'rotate-180' : ''"
              >
                ▼
              </span>
            </button>
            <div 
              v-if="faq.open"
              class="px-6 pb-6 text-white/60 text-sm leading-relaxed border-t border-white/5 pt-4"
            >
              {{ faq.a }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== FINAL CTA ===== -->
    <section class="bg-[#27272a] px-6 py-20 relative overflow-hidden">
      <div class="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div class="h-[600px] w-[600px] rounded-full bg-emerald-600/10 blur-[160px]"></div>
      </div>

      <div class="mx-auto max-w-4xl text-center relative z-10">
        <h2 class="text-3xl md:text-4xl font-extrabold text-white leading-tight">
          Planning Your <span class="text-emerald-400">Umrah or Ramadan Travel</span> in Saudi Arabia?
        </h2>

        <p class="mx-auto mt-4 max-w-2xl text-white/70 text-sm md:text-base leading-relaxed">
          Book premium airport and intercity transfers with confidence.
        </p>

        <button
          @click="scrollToForm"
          class="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 px-8 py-4 text-base font-semibold text-[#020617] shadow-lg shadow-emerald-500/30 hover:bg-emerald-400 transition"
        >
          Get Instant Quote
          <span class="text-xl">↗</span>
        </button>
      </div>
    </section>

    <!-- ===== FOOTER ===== -->
    <footer class="bg-[#121212] px-6 py-8 border-t border-white/10">
      <div class="mx-auto max-w-6xl text-center text-[12px] text-white/40">
        © 2025 Vehitra World • All Rights Reserved  
        <br />
        <span class="text-white/30">Privacy Policy · Terms · Contact</span>
      </div>
    </footer>

    <!-- ===== CUSTOM OVERLAY ALERT ===== -->
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div v-if="alert.show" class="fixed inset-0 z-[100000] flex items-center justify-center px-4">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="alert.show = false"></div>
        <div class="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-[#0f1317]/90 p-8 text-center shadow-2xl backdrop-blur-xl">
          <div class="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-400">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 class="text-xl font-bold text-white">{{ alert.title }}</h3>
          <p class="mt-3 text-sm leading-relaxed text-white/60">
            {{ alert.message }}
          </p>
          <button
            @click="alert.show = false"
            class="mt-8 w-full rounded-full bg-emerald-500 py-4 text-sm font-bold text-[#050708] shadow-lg shadow-emerald-500/20 transition hover:bg-emerald-400 active:scale-95"
          >
            Got it, thanks
          </button>
        </div>
      </div>
    </Transition>

  </div>
</template>

<script setup lang="ts">
import {
  reactive,
  ref,
  defineComponent,
  h,
  onMounted,
  onBeforeUnmount,
  computed,  
} from 'vue'
import { useRouter } from 'vue-router'
import LocationField from '@/components/search/LocationField.vue'
import DateField from '@/components/search/DateField.vue'
import TimeField from '@/components/search/TimeField.vue'
import { getBackendBaseUrl } from '@/utils/common'

import heroImageUrl from '@/assets/landing/saudi/makkah-hero-bg.png'
const heroImage = heroImageUrl

import vwLogo from '@/assets/logo.png'

// Vehicle & route images
import sClassImg from '@/assets/landing/uae/s-class.jpg'
import vClassImg from '@/assets/landing/uae/v-class.jpg'
import eClassImg from '@/assets/landing/uae/e-class.jpg'
// Using proper GMC image
import gmcImg from '@/assets/landing/saudi/gmc-suv.png'
const suvImg = gmcImg 

import saudiImg from '@/assets/landing/uae/saudi.jpg'
import qatarImg from '@/assets/landing/uae/qatar-gcc.jpg'
import europeImg from '@/assets/landing/uae/europe-usa.jpg'

// Unique Saudi Route Images
import jeddahMakkahImg from '@/assets/landing/saudi/jeddah-makkah.png'
import madinahMakkahImg from '@/assets/landing/saudi/madinah-makkah.png'
import makkahMadinahImg from '@/assets/landing/saudi/makkah-madinah.png'
import hotelHaramImg from '@/assets/landing/saudi/hotel-haram.png'

const router = useRouter()
const currentStep = ref(1)
const submitting = ref(false)
const carTypeOptions = ['Premium', 'Luxury', 'SUV', 'Van']

const alert = reactive({
  show: false,
  title: 'Attention',
  message: ''
})

const showAlert = (message: string, title = 'Attention') => {
  alert.title = title
  alert.message = message
  alert.show = true
}

const validateAndNext = () => {
  if (currentStep.value === 1) {
    if (!form.name || !form.phone) {
      showAlert('Please enter your full name and a valid contact number so we can reach out with your quote.', 'Incomplete Details')
      return
    }
  } else if (currentStep.value === 2) {
    if (!form.pickup || !form.dropoff || !pickUp.date || !pickUp.time) {
      showAlert('Please provide your pickup location, destination, and preferred date/time to calculate your quote.', 'Trip Info Required')
      return
    }
  }
  currentStep.value++
}

const faqs = reactive([
  {
    q: 'Do you support Umrah pilgrims during Ramadan?',
    a: 'Yes, we specialize in Ramadan and Umrah transfers, providing dedicated coordination and premium vehicles for pilgrims.',
    open: false
  },
  {
    q: 'Can you handle big groups for events in saudi?',
    a: 'Absolutely. We have a fleet of Mercedes V-Class and can arrange multiple vehicles or premium minibuses for larger groups and delegations.',
    open: false
  },
  {
    q: 'Do you provide Meet & Greet at Jeddah & Madinah airports?',
    a: 'Yes, our chauffeurs provide professional Meet & Greet assistance at Jeddah, Madinah, and Riyadh airports.',
    open: false
  },
  {
    q: 'Which vehicles are suitable for families?',
    a: 'The Mercedes V-Class and GMC/SUV are ideal for families, offering ample space for passengers and luggage.',
    open: false
  },
  {
    q: 'Can I book via WhatsApp?',
    a: 'Yes, you can click the WhatsApp button to chat with our team directly for bookings and inquiries.',
    open: false
  }
])

const form = reactive({
  name: '',
  phone: '',
  email: '',
  pickup: '',
  dropoff: '',
  datetime: '',
  notes: '',
  carType: '',
  passengers: 0,
  suitcases: 0,
})

// Whats app
const WHATSAPP_NUMBER = '97332141440' // no +
const whatsappMessage =
  'Hi, I would like to inquire about Saudi Umrah/Ramadan transfers.'

const whatsappUrl = computed(
  () =>
    `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappMessage)}`
)

const vehicleCards = [
  {
    name: 'Mercedes S-Class',
    image: sClassImg,
    description: [
      'VIP Umrah guests & dignitaries',
      'Maximum comfort for long journeys'
    ],
    tags: ['3 PASSENGERS', '2 LUGGAGE', 'VIP'],
  },
  {
    name: 'Mercedes V-Class',
    image: vClassImg,
    description: [
      'Families & group pilgrims',
      'Spacious with luggage for Umrah trips'
    ],
    tags: ['5 PASSENGERS', '5 LUGGAGE', 'SPACIOUS'],
  },
  {
    name: 'Mercedes E-Class',
    image: eClassImg,
    description: [
      'Executive & business pilgrims',
      'Comfortable airport transfers'
    ],
    tags: ['3 PASSENGERS', '2 LUGGAGE', 'EXECUTIVE'],
  },
  {
    name: 'GMC / Premium SUV',
    image: suvImg,
    description: [
      'Ideal for road trips Jeddah ↔ Makkah ↔ Madinah',
      'Strong AC, extra luggage space'
    ],
    tags: ['5 PASSENGERS', '4 LUGGAGE', 'PREMIUM SUV'],
  },
]

const routeCards = [
  {
    name: 'Jeddah Airport → Makkah',
    image: jeddahMakkahImg,
  },
  {
    name: 'Madinah Airport → Makkah',
    image: madinahMakkahImg,
  },
  {
    name: 'Makkah ↔ Madinah',
    image: makkahMadinahImg,
  },
  {
    name: 'Hotel ↔ Haram Transfers',
    image: hotelHaramImg,
  },
]

const whyCards = [
  {
    title: 'Saudi Focused Operations',
    copy: 'Dedicated coordination for Umrah & Ramadan travel across the Kingdom.',
  },
  {
    title: 'Premium Fleet Only',
    copy: 'Mercedes & GMC vehicles only – we do not offer economy cars for these services.',
  },
  {
    title: 'Professional Chauffeurs',
    copy: 'Trained for pilgrim travel & religious etiquette, ensuring a respectful journey.',
  },
  {
    title: 'Meet & Greet at Airports',
    copy: 'Full assistance from your arrival at the terminal until you reach your vehicle.',
  },
  {
    title: '24/7 WhatsApp Support',
    copy: 'Live support in Arabic & English whenever you need assistance.',
  },
  {
    title: 'Fixed Transparent Pricing',
    copy: 'No hidden costs or surprises, even during peak Ramadan season.',
  },
]

const pickUp = reactive({
  location: { id: '', name: '', isAirport: false, coordinates: { lat: null, lng: null } },
  date: null as string | null,
  time: '' as string,
})
const dropOff = reactive({
  location: { id: '', name: '', isAirport: false, coordinates: { lat: null, lng: null } },
})

const onAddressSelected = (address: any, type: 'pickUp' | 'dropOff') => {
  const add = address ?? {
    id: '',
    name: '',
    description: '',
    coordinates: { lat: null, lng: null },
  }
  if (type === 'pickUp') {
    pickUp.location = add
    form.pickup = add?.name || add?.description || ''
  } else {
    dropOff.location = add
    form.dropoff = add?.name || add?.description || ''
  }
}
const onDateChanged = (d: string) => {
  pickUp.date = d
  form.datetime = [pickUp.date, pickUp.time].filter(Boolean).join(' ')
}
const onTimeChanged = (t: string) => {
  pickUp.time = t
  form.datetime = [pickUp.date, pickUp.time].filter(Boolean).join(' ')
}

const scrollToForm = () => {
  const els = document.querySelectorAll('form')
  if (els && els[0])
    els[0].scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const submitLead = async () => {
  if (!form.name || !form.phone || !form.pickup || !form.dropoff || !form.datetime) {
    showAlert('Some required fields are missing. Please go back and ensure all trip details are filled.', 'Form Error')
    return
  }
  
  const API_BASE = getBackendBaseUrl()
  const payload = {
    name: form.name,
    phone: form.phone,
    email: form.email || null,
    pickup: form.pickup,
    dropoff: form.dropoff,
    datetime: form.datetime,
    notes: form.notes || null,
    source: 'saudi-umrah-ramadan',
    car_type: form.carType || null,
    passengers: Number(form.passengers) || 0,
    suitcases: Number(form.suitcases) || 0,
    pickUp,
    dropOff,
  }

  submitting.value = true
  try {
    const res = await fetch(`${API_BASE}api/leads/airport-transfer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
    if (!res.ok) {
      const msg = await res.text().catch(() => '')
      throw new Error(`Request failed (${res.status}) ${msg}`)
    }
    router.push({ name: 'ThankYou' })
  } catch (e: any) {
    showAlert(e?.message || 'Something went wrong while submitting your request. Please try again or contact us via WhatsApp.', 'Submission Failed')
  } finally {
    submitting.value = false
  }
}

// Google Places dropdown patch (keeps your styling)
function usePatchPacDropdown() {
  let rafId: number | null = null;
  let running = false;
  const getFocusedInput = (): HTMLInputElement | null =>
    document.activeElement?.tagName === 'INPUT'
      ? (document.activeElement as HTMLInputElement)
      : (document.querySelector('.lead-form input:focus') as HTMLInputElement | null)

  const apply = () => {
    const pacs = document.querySelectorAll<HTMLElement>('.pac-container')
    if (!pacs.length) return
    const input = getFocusedInput() || (document.querySelector('.lead-form input') as HTMLInputElement | null)
    if (!input) return
    const r = input.getBoundingClientRect()
    const desiredWidth = Math.max(r.width + 40, 560)
    pacs.forEach(pac => {
      pac.style.setProperty('position', 'fixed', 'important')
      pac.style.setProperty('left', `${r.left}px`, 'important')
      pac.style.setProperty('top', `${r.bottom}px`, 'important')
      pac.style.setProperty('width', `${desiredWidth}px`, 'important')
      pac.style.setProperty('minWidth', `${desiredWidth}px`, 'important')
      pac.style.setProperty('maxWidth', `${desiredWidth}px`, 'important')
      pac.style.setProperty('maxHeight', '480px', 'important')
      pac.style.setProperty('overflowY', 'auto', 'important')
      pac.style.setProperty('overflowX', 'hidden', 'important')
      pac.style.setProperty('zIndex', '99999', 'important')
      pac.style.setProperty('borderRadius', '12px', 'important')
      pac.style.setProperty('border', '1px solid rgba(255,255,255,.18)', 'important')
      pac.style.setProperty('background', 'rgba(15,19,23,.98)', 'important')
      pac.style.setProperty('boxShadow', '0 12px 40px rgba(0,0,0,.5)', 'important')
      pac.style.setProperty('padding', '4px 0', 'important')
    })
  }
  const loop = () => { apply(); if (running) rafId = requestAnimationFrame(loop) }
  const start = () => { if (!running) { running = true; loop() } }
  const stop  = () => { running = false; if (rafId) cancelAnimationFrame(rafId); rafId = null }
  const onFocusIn  = () => setTimeout(() => { apply(); start() }, 0)
  const onFocusOut = () => setTimeout(stop, 150)
  const onResize   = () => apply()
  const onScroll   = () => apply()

  onMounted(() => {
    document.addEventListener('focusin', onFocusIn, true)
    document.addEventListener('focusout', onFocusOut, true)
    window.addEventListener('resize', onResize)
    window.addEventListener('scroll', onScroll, true)
    const mo = new MutationObserver(apply)
    mo.observe(document.body, { childList: true, subtree: true })
  })
  onBeforeUnmount(() => {
    document.removeEventListener('focusin', onFocusIn, true)
    document.removeEventListener('focusout', onFocusOut, true)
    window.removeEventListener('resize', onResize)
    window.removeEventListener('scroll', onScroll, true)
    stop()
  })
}
usePatchPacDropdown()

/* ---------- Lightweight local components ---------- */
const TextField = defineComponent({
  name: 'TextField',
  props: { label: String, modelValue: [String, Number], placeholder: String, type: { type: String, default: 'text' }, min: [String, Number] },
  emits: ['update:modelValue'],
  setup(props, { emit, attrs }) {
    const onInput = (e: any) => emit('update:modelValue', e.target.value)
    return () => h('label', { class: 'block', ...attrs }, [
      h('div', { class: 'mb-1 text-[11px] text-white/70 font-semibold' }, props.label),
      h('input', {
        class: 'w-full rounded-xl border border-[var(--lead-border)] bg-[var(--lead-fill)] px-4 text-sm outline-none placeholder:text-white/30 focus:border-white/30',
        style: { height: 'var(--lead-input-h)' },
        value: props.modelValue, placeholder: props.placeholder, onInput,
        type: props.type, min: props.min
      })
    ])
  }
})

const NumberField = defineComponent({
  name: 'NumberField',
  props: { label: String, modelValue: [String, Number], min: { type: [String, Number], default: 0 } },
  emits: ['update:modelValue'],
  setup(props, { emit, attrs }) {
    const onInput = (e: any) => emit('update:modelValue', e.target.value === '' ? '' : Number(e.target.value))
    return () => h('label', { class: 'block', ...attrs }, [
      h('div', { class: 'mb-1 text-[11px] text-white/70 font-semibold' }, props.label),
      h('input', {
        class: 'w-full rounded-xl border border-[var(--lead-border)] bg-[var(--lead-fill)] px-4 text-sm outline-none placeholder:text-white/30 focus:border-white/30',
        style: { height: 'var(--lead-input-h)' },
        value: props.modelValue, type: 'number', min: props.min, onInput
      })
    ])
  }
})

const SelectField = defineComponent({
  name: 'SelectField',
  props: { label: String, modelValue: String, options: Array as () => string[], placeholder: String },
  emits: ['update:modelValue'],
  setup(props, { emit, attrs }) {
    const onChange = (e: any) => emit('update:modelValue', e.target.value)
    return () => h('label', { class: 'block', ...attrs }, [
      h('div', { class: 'mb-1 text-[11px] text-white/70 font-semibold' }, props.label),
      h('select', {
        class: 'w-full rounded-xl border border-[var(--lead-border)] bg-[var(--lead-fill)] px-4 text-sm outline-none focus:border-white/30',
        style: { height: 'var(--lead-input-h)' },
        value: props.modelValue ?? '',
        onChange
      }, [
        h('option', { value: '' }, props.placeholder || 'Select…'),
        ...(props.options || []).map(o => h('option', { value: o }, o))
      ])
    ])
  }
})

const TextArea = defineComponent({
  name: 'TextArea',
  props: { label: String, modelValue: String, placeholder: String },
  emits: ['update:modelValue'],
  setup(props, { emit, attrs }) {
    const onInput = (e: any) => emit('update:modelValue', e.target.value)
    return () => h('label', { class: 'block', ...attrs }, [
      h('div', { class: 'mb-1 text-[11px] text-white/70 font-semibold' }, props.label),
      h('textarea', {
        class: 'min-h-[120px] w-full rounded-xl border border-[var(--lead-border)] bg-[var(--lead-fill)] p-4 text-sm outline-none placeholder:text-white/30 focus:border-white/30',
        value: props.modelValue, placeholder: props.placeholder, onInput
      })
    ])
  }
})
</script>

<style scoped>
:global(:root) {
  --lead-input-h: 56px;
  --lead-border: rgba(255,255,255,.14);
  --lead-fill: rgba(255,255,255,.06);
  --lead-focus: rgba(255,255,255,.32);
}
.lead-card-wrap { width: 100%; }
@screen md { .lead-card-wrap { @apply max-w-[560px] justify-self-end; } }

.lead-card {
  @apply rounded-2xl backdrop-blur-2xl p-6 md:p-8;
  background: linear-gradient(180deg, rgba(255,255,255,.10), rgba(255,255,255,.06));
  border: 1px solid rgba(255,255,255,.18);
  box-shadow: inset 0 1px 0 rgba(255,255,255,.12), 0 20px 60px rgba(0,0,0,.35);
}
.btn-primary {
  @apply w-full rounded-full font-semibold transition px-5 py-4 text-[#0a0d0f] bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60;
  font-size: 16px;
}

/* ===== Resets for embedded fields ===== */
.lead-form :deep(.af-loc.relative) { padding: 0 !important; overflow: visible !important; }
.lead-form :deep(.af-loc .flex.flex-col.space-y-2) { gap: 6px !important; }
.lead-form :deep(.af-loc .text-small),
.lead-form :deep(.af-loc .text-small *),
.lead-form :deep(.af-loc [class*="uppercase"]) {
  text-transform: none !important;
  font-size: 11px !important;
  line-height: 1.2 !important;
  letter-spacing: .02em !important;
  color: rgba(255,255,255,.70) !important;
  font-weight: 600 !important;
}
.lead-form :deep(.af-loc input[type="text"]) {
  height: var(--lead-input-h) !important;
  min-height: var(--lead-input-h) !important;
  border-radius: 12px !important;
  border: 1px solid var(--lead-border) !important;
  background: var(--lead-fill) !important;
  color: #fff !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  padding: 0 16px !important;
  outline: none !important; box-shadow: none !important;
}
.lead-form :deep(.af-loc .fa-times-circle),
.lead-form :deep(.af-loc .fa-circle-xmark),
.lead-form :deep(.af-loc [data-icon="times-circle"]),
.lead-form :deep(.af-loc [data-icon="circle-xmark"]),
.lead-form :deep(.af-loc .clear-btn) { display: none !important; }

.lead-form :deep(.af-loc > .relative > ul[class*="absolute"][class*="ring-"]) {
  left: 0 !important; right: auto !important; top: 100% !important;
  width: 100% !important; margin-left: 0 !important;
  max-height: 520px !important; overflow-y: auto !important; overflow-x: hidden !important;
  background: rgba(15,19,23,.98) !important;
  border: 1px solid rgba(255,255,255,.18) !important;
  border-radius: 12px !important;
  box-shadow: 0 12px 40px rgba(0,0,0,.5) !important;
  padding: 4px 0 !important; z-index: 10000 !important;
}
@media (min-width: 768px) {
  .lead-form :deep(.af-loc > .relative > ul[class*="absolute"][class*="ring-"]) {
    width: calc(100% + 40px) !important; margin-left: -20px !important;
  }
}
.lead-form :deep(.af-loc > .relative > ul[class*="absolute"][class*="ring-"] > li) {
  display: flex !important; align-items: flex-start !important; gap: 10px !important;
  padding: 12px 16px !important; border-bottom: 1px solid rgba(255,255,255,.06) !important;
  color: #d8dee9 !important; font-size: 15px !important; line-height: 1.4 !important; cursor: pointer !important;
}
.lead-form :deep(.af-loc > .relative > ul[class*="absolute"][class*="ring-"] > li:hover) { background: rgba(255,255,255,.08) !important; }
.lead-form :deep(.af-loc > .relative > ul[class*="absolute"][class*="ring-"] > li:last-child) { border-bottom: 0 !important; }
.lead-form :deep(.af-loc > .relative > ul[class*="absolute"][class*="ring-"] > li svg) {
  color: #9fb7ff !important; margin-top: 2px !important;
}

.lead-form :deep(.af-dt), .lead-form :deep(.af-dt [class*="p-"]), .lead-form :deep(.af-dt [class*="px-"]), .lead-form :deep(.af-dt [class*="py-"]), .lead-form :deep(.af-dt [class*="m-"]) {
  padding: 0 !important; margin: 0 !important;
}
.lead-form :deep(.af-dt label), .lead-form :deep(.af-dt .label), .lead-form :deep(.af-dt [class*="uppercase"]) {
  text-transform: none !important; margin-bottom: 6px !important;
  font-size: 11px !important; line-height: 1.2 !important; letter-spacing: .02em !important;
  color: rgba(255,255,255,.70) !important; font-weight: 600 !important;
}
.lead-form :deep(.af-dt input), .lead-form :deep(.af-dt input[type="text"]), .lead-form :deep(.af-dt input[type="date"]), .lead-form :deep(.af-dt input[type="time"]) {
  width: 100% !important; height: var(--lead-input-h) !important; min-height: var(--lead-input-h) !important;
  border-radius: 12px !important; border: 1px solid var(--lead-border) !important; background: var(--lead-fill) !important;
  color: #fff !important; font-size: 15px !important; font-weight: 500 !important; padding: 0 16px !important;
  outline: none !important; box-shadow: none !important; color-scheme: dark;
}
.lead-form :deep(.af-dt input::placeholder) { color: rgba(255,255,255,.38) !important; font-weight: 500 !important; }
.lead-form :deep(.af-dt input[type="date"]::-webkit-calendar-picker-indicator),
.lead-form :deep(.af-dt input[type="time"]::-webkit-calendar-picker-indicator) { filter: invert(1) opacity(.85); }
.lead-form :deep(.af-dt.relative) { overflow: visible !important; }

/* Hide the green clear (X) badge inside LocationField */
.lead-form :deep(.af-loc .bg-emerald-500),
.lead-form :deep(.af-loc .bg-emerald-600) {
  position: absolute !important;
  right: -9999px !important;
  opacity: 0 !important;
  pointer-events: none !important;
  width: 0 !important;
  height: 0 !important;
}

/* Why Card Glass Effect */
.why-card {
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.why-card:hover {
  transform: translateY(-8px);
}
.why-card::after {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 50%;
  height: 100%;
  background: linear-gradient(
    to right,
    transparent,
    rgba(255, 255, 255, 0.05),
    transparent
  );
  transform: skewX(-25deg);
  transition: 0.7s;
}
.why-card:hover::after {
  left: 150%;
}
</style>

<style>
.pac-item { color: #d8dee9 !important; font-size: 15px !important; line-height: 1.4 !important;
  padding: 12px 16px !important; white-space: nowrap !important; overflow: hidden !important; text-overflow: ellipsis !important; }
.pac-item:hover { background: rgba(255,255,255,.08) !important; }
.pac-item-query { font-weight: 700 !important; color: #fff !important; }
.pac-icon { filter: brightness(0) invert(1) !important; margin-right: 8px !important; }
</style>
